import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Play, RotateCcw, CheckCircle, Edit } from 'lucide-react';
import { Tutorial } from '../types';
import { cn } from '../utils/cn';

interface TutorialCardProps {
  tutorial: Tutorial;
  progress?: number; // 0-100 percentage
  onStart?: () => void;
  onContinue?: () => void;
  onEdit?: () => void;
  showEditButton?: boolean;
}

const TutorialCard: React.FC<TutorialCardProps> = ({ 
  tutorial, 
  progress = 0, 
  onStart, 
  onContinue,
  onEdit,
  showEditButton = false
}) => {
  const isStarted = progress > 0;
  const isCompleted = progress === 100;

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'advanced':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Getting Started':
        return 'bg-blue-100 text-blue-800';
      case 'Product Guides':
        return 'bg-purple-100 text-purple-800';
      case 'API Docs':
        return 'bg-orange-100 text-orange-800';
      case 'Workflows':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <motion.div
      className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden group hover:shadow-xl transition-all duration-300"
      whileHover={{ scale: 1.02 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] p-6 text-white">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold mb-2 line-clamp-2">{tutorial.title}</h3>
            <p className="text-blue-100 text-sm line-clamp-1">
              {tutorial.description && tutorial.description.length > 80 
                ? `${tutorial.description.substring(0, 80)}...` 
                : tutorial.description || 'No description available'
              }
            </p>
          </div>
          {isCompleted && (
            <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0 ml-4" />
          )}
        </div>
        
        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          <span className={cn(
            "px-3 py-1 rounded-full text-xs font-medium border",
            getDifficultyColor(tutorial.difficulty)
          )}>
            {tutorial.difficulty}
          </span>
          <span className={cn(
            "px-3 py-1 rounded-full text-xs font-medium",
            getCategoryColor(tutorial.category)
          )}>
            {tutorial.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Time and Progress */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center text-gray-600">
            <Clock className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">{tutorial.estimatedTime}</span>
          </div>
          <div className="text-sm text-gray-500">
            {tutorial.steps?.length || 0} steps
          </div>
        </div>

        {/* Progress Bar */}
        {isStarted && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Progress</span>
              <span className="text-sm text-gray-500">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                className="bg-gradient-to-r from-[#667eea] to-[#764ba2] h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, delay: 0.2 }}
              />
            </div>
          </div>
        )}

        {/* Steps Preview */}
        {tutorial.steps && tutorial.steps.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">What you'll learn:</h4>
            <div className="space-y-2">
              {tutorial.steps.slice(0, 3).map((step, index) => (
                <div key={step.id} className="flex items-start space-x-2">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-[#667eea] to-[#764ba2] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs text-white font-medium">{index + 1}</span>
                  </div>
                  <span className="text-sm text-gray-600 line-clamp-1">{step.title}</span>
                </div>
              ))}
              {tutorial.steps.length > 3 && (
                <div className="text-sm text-gray-500 ml-7">
                  +{tutorial.steps.length - 3} more steps
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Button */}
        <motion.button
          className={cn(
            "w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2",
            isCompleted
              ? "bg-green-100 text-green-800 hover:bg-green-200"
              : isStarted
              ? "bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white hover:from-[#5a6fd8] hover:to-[#6a4190]"
              : "bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white hover:from-[#5a6fd8] hover:to-[#6a4190]"
          )}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={isCompleted ? undefined : isStarted ? onContinue : onStart}
        >
          {isCompleted ? (
            <>
              <CheckCircle className="w-4 h-4" />
              <span>Completed</span>
            </>
          ) : isStarted ? (
            <>
              <RotateCcw className="w-4 h-4" />
              <span>Continue</span>
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              <span>Start Tutorial</span>
            </>
          )}
        </motion.button>

        {/* Edit Button (if enabled) */}
        {showEditButton && onEdit && (
          <motion.button
            className="w-full mt-3 py-2 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 hover:bg-gray-200"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onEdit}
          >
            <Edit className="w-4 h-4" />
            <span>Edit Tutorial</span>
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default TutorialCard;
